import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PACK_EVENTS, packCancellation, packEligibility, packHistory, packLedgerBalanceLabel } from '../../../demo/lib/customer-packs/presentation';

import { ApiError } from '../api/client';
import {
  listCustomerSubscriptions,
  listSubscriptionPlans,
  sellSubscription,
} from '../api/endpoints/subscriptions';
import type { ChargePaymentMethod, CustomerSubscription } from '../api/schemas';
import { usePermissions } from '../auth/usePermissions';
import { invoiceOutcome } from '../lib/autoInvoice';
import { ErrorBanner } from './ErrorBanner';
import { formatDateTime, formatMoneyCents } from '../lib/format';
// Selling a paquete carries no reference, so it offers only the methods that need none.
import { PAYMENT_METHODS_WITHOUT_REFERENCE as METHOD_OPTIONS, paymentMethodLabel } from '../lib/paymentMethods';
import { useBusiness } from '../lib/useBusiness';
import { useMutationAttempt } from '../lib/use-mutation-attempt';

const PAYMENT_METHODS: ChargePaymentMethod[] = METHOD_OPTIONS.map((entry) => entry.value);

type CustomerSubscriptionsProps = { customerId: string };

/**
 * One customer's Suscripciones inside their file, plus the sale. Selling is
 * also how a renewal happens: the same plan sold again, which the server
 * records as a Cobro so it lands in Finanzas without a second money path.
 */
export function CustomerSubscriptions({ customerId }: CustomerSubscriptionsProps) {
  const queryClient = useQueryClient();
  const [selling, setSelling] = useState(false);
  const [planId, setPlanId] = useState('');
  const [method, setMethod] = useState<ChargePaymentMethod>('efectivo');
  const [invoiceNote, setInvoiceNote] = useState<string | undefined>();
  const canInvoice = usePermissions().can('facturas');

  const subscriptionsQuery = useQuery({
    queryKey: ['customer-subscriptions', customerId],
    queryFn: () => listCustomerSubscriptions(customerId),
  });
  const plansQuery = useQuery({
    queryKey: ['business-subscription-plans'],
    queryFn: () => listSubscriptionPlans(false),
    enabled: selling,
  });

  const sellMutation = useMutationAttempt({
    mutationFn: async (input: { customerId: string; planId: string; paymentMethod: ChargePaymentMethod }, key: string) => {
      const result = await sellSubscription(input.customerId, { planId: input.planId, paymentMethod: input.paymentMethod }, key);
      void queryClient.invalidateQueries({ queryKey: ['customer-subscriptions', input.customerId] });
      void queryClient.invalidateQueries({ queryKey: ['business-charges'] });
      // A partial write keeps the original input and key for charge recovery.
      if (result.chargeError) throw new ApiError(503, result.chargeError.code,
        `La suscripción quedó registrada, pero el cobro no: ${result.chargeError.message}`);
      return result;
    },
    exito: 'Plan vendido',
    onSuccess: (result) => {
      setInvoiceNote(invoiceOutcome(result.data.charge, canInvoice));
      setSelling(false);
      setPlanId('');
    },
  });

  const subscriptions = subscriptionsQuery.data?.data ?? [];
  const plans = plansQuery.data?.data ?? [];
  const now = new Date();
  const saleLocked = sellMutation.isPending || sellMutation.uncertain;

  return (
    <section className="section" aria-label="Suscripciones">
      <h2 className="section-label">Suscripciones</h2>

      {subscriptionsQuery.isError ? <ErrorBanner message="No pudimos cargar las suscripciones." /> : null}
      {plansQuery.isError ? <ErrorBanner message="No pudimos cargar los planes." /> : null}
      {subscriptions.length === 0 && subscriptionsQuery.isSuccess ? (
        <p className="row-sub">Todavía no ha comprado ningún plan.</p>
      ) : (
        <ul className="rows">
          {subscriptions.map((subscription) => (
            <li key={subscription.id} className="row">
              <div className="grow">
                <p className="row-title">{subscription.planName}</p>
                <p className="row-sub">{describe(subscription, now)}</p>
                <SubscriptionDetails subscription={subscription} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {sellMutation.isError ? <ErrorBanner message={errorMessage(sellMutation.error)} /> : null}
      {invoiceNote ? (
        <p className="row-sub" role="status">
          {invoiceNote}
        </p>
      ) : null}

      {selling ? (
        <div className="card form-grid cols-2 sell-form">
          <label className="span-2">
            <span className="field-label">Plan</span>
            <select value={planId} disabled={saleLocked} onChange={(event) => setPlanId(event.target.value)}>
              <option value="">Elige un plan</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} · {formatMoneyCents(plan.priceCents)}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            <span className="field-label">Cómo paga</span>
            <select aria-label="Método de pago" value={method} disabled={saleLocked}
              onChange={(event) => setMethod(event.target.value as ChargePaymentMethod)}>
              {PAYMENT_METHODS.map((entry) => (
                <option key={entry} value={entry}>{paymentMethodLabel(entry)}</option>
              ))}
            </select>
          </label>
          <div className="action-pair span-2">
            <button
              type="button"
              className="btn"
              disabled={!planId || sellMutation.isPending}
              onClick={() => sellMutation.uncertain ? sellMutation.retry() : sellMutation.mutate({ customerId, planId, paymentMethod: method })}
            >
              {sellMutation.isPending ? 'Vendiendo…' : 'Vender plan'}
            </button>
            <button type="button" className="btn secondary" disabled={saleLocked} onClick={() => setSelling(false)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn small secondary" onClick={() => setSelling(true)}>
          {subscriptions.length === 0 ? 'Vender un plan' : 'Vender o renovar'}
        </button>
      )}
    </section>
  );
}

export function SubscriptionDetails({ subscription }: { subscription: CustomerSubscription }) {
  const business = useBusiness();
  const history = packHistory(subscription);
  return <>
    {subscription.balances ? <p className="row-sub">{packLedgerBalanceLabel(subscription, subscription.balances)}</p> : null}
    <details>
      <summary className="btn secondary">Detalles y movimientos</summary>
      <p className="row-sub">{packEligibility(subscription, business?.services ?? []).join(' · ')}</p>
      {subscription.sessionOnly ? <p className="row-sub">Solo en clases con fecha y horario.</p> : null}
      <p className="row-sub">{packCancellation(subscription)}</p>
      <p className="row-sub">Este paquete no se renueva automáticamente.</p>
      <ul className="rows" aria-label={`Movimientos de ${subscription.planName}`}>
        {history.map(entry => <li key={entry.eventKey} className="row">
          <div className="grow">
            <p className="row-title">{PACK_EVENTS[entry.event]}</p>
            <p className="row-sub">{formatDateTime(entry.occurredAt)}</p>
            <p className="row-sub">{packLedgerBalanceLabel(subscription, entry.balances)}</p>
          </div>
        </li>)}
      </ul>
      {!history.length ? <p className="row-sub">Todavía no hay movimientos registrados.</p> : null}
    </details>
  </>;
}

/** Plain Spanish for what this subscription is worth right now. */
function describe(subscription: CustomerSubscription, now: Date): string {
  const expired = new Date(subscription.expiresAt).getTime() <= now.getTime();
  if (subscription.status === 'cancelled') return 'Cancelada';
  if (expired) return `Vencida el ${formatDateTime(subscription.expiresAt)}`;
  if (subscription.kind === 'unlimited') {
    return `Ilimitado hasta el ${formatDateTime(subscription.expiresAt)}`;
  }
  const left = subscription.creditsRemaining ?? 0;
  if (left === 0) return `Sin clases, vence el ${formatDateTime(subscription.expiresAt)}`;
  const noun = left === 1 ? 'clase' : 'clases';
  return `${left} de ${subscription.creditsTotal} ${noun} · vence el ${formatDateTime(subscription.expiresAt)}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos vender el plan.';
}
